import {useTransition} from '@logistics-frontend/blocks'
import {useFormContext} from '@logistics-frontend/form/v2'
import {useCallback, useMemo} from 'react'
import {useParams} from 'react-router-dom'

import {fetchSharedRoutePoll, Poll, PollQuestion, PollQuestionAnswer} from 'src/api'

import {RouterParams} from '../../types'
import {AnswerProps} from './Answer'
import {FormType} from './Form.types'
import {formToQuestionChoice, getNewValues} from './Poll.utils'
import {getSubPollQuestions} from './utils'

type UsePoll = Pick<AnswerProps, 'handleAnswerClick'> & {
  isPollCompleted: boolean
  questions: PollQuestion[]
}

export const usePoll = (poll: Poll): UsePoll => {
  const {sharingKey = ''} = useParams<RouterParams>()
  const {values: formValues, setFieldValue} = useFormContext<FormType>()
  const [, startTransition] = useTransition(undefined, {key: 'sharing-route/poll'})
  const questions = useMemo(() => getSubPollQuestions(poll, formValues), [poll, formValues])

  const handleAnswerClick = useCallback(
    (question: PollQuestion, answer: PollQuestionAnswer) => {
      const currentAnswerValues = formValues[question.id]
      const newValues = getNewValues(currentAnswerValues, answer.id, question.choices_type)
      setFieldValue(question.id, newValues)
      startTransition(() =>
        fetchSharedRoutePoll(
          sharingKey,
          formToQuestionChoice(poll.subpolls, {...formValues, [question.id]: newValues}),
        ),
      )
    },
    [formValues, poll.subpolls, setFieldValue, sharingKey, startTransition],
  )

  const isPollCompleted = !!Object.values(formValues).length

  return {handleAnswerClick, isPollCompleted, questions}
}
